import type { ICameraController, IRaycaster, IViewPortController } from '@/engine/app/interfaces';
import type { IHandlerDescriptor, IPointerController, IPointerHandler } from './PointerController';
import type { SyntheticEvent } from './SyntheticEvent';
import type { PointerSyntheticEvent } from './PointerSyntheticEvent';
import { PointerActionFactory } from './pointerHandlerAdjustCamera/PointerActionFactory';
import * as THREE from 'three';

export interface IPointerAction {
  addPointer: (event: PointerSyntheticEvent) => void;
  removePointer: (event: PointerSyntheticEvent) => boolean; // returns true if action is finalized and could be disposed
  updatePointer: (event: PointerSyntheticEvent) => boolean; // returns true if update is handled
}

export interface IPointerActionFactory {
  createPointerAction: (
    event: PointerSyntheticEvent,
    parent: IPointerController,
    cameraModifier: ICameraModifier,
    noRotate: boolean,
    rotateAroundCenterZoom: number
  ) => IPointerAction;
}

export interface ICameraModifier {
  getPointerEventCoordinates: (event: PointerEvent | WheelEvent) => THREE.Vector2;
  getFrameDimensions: () => THREE.Vector2;
  getCenterPoint: () => THREE.Vector3;
  getCameraZoom: () => number;
  rotateCamera: (ptOfInterest3d: THREE.Vector3, ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2) => void;
  rotateCameraInPlane: (ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2) => void;
  rotateCameraInPlaneDirect: (ptOfInterest2d: THREE.Vector2, vecFrom2d: THREE.Vector2, vecTo2d: THREE.Vector2) => void;
  panCamera: (ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2) => void;
  zoomCamera: (ptOfInterest2d: THREE.Vector2, ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2) => void;
  zoomCameraDirect: (ptOfInterest2d: THREE.Vector2, zoomAmount: number) => void;
  notifyCameraChanged: () => void;
}

export class PointerHandlerAdjustCamera implements IPointerHandler, ICameraModifier {
  private readonly _viewPortController: IViewPortController;

  private readonly _cameraController: ICameraController;

  private readonly _actionFactory: IPointerActionFactory;

  private readonly _noWheelZoom: boolean;

  private readonly _rotateAroundCenterZoom = 2.5;

  private _noRotate: boolean;

  private _activeAction?: IPointerAction;

  private _handlerLock?: IHandlerDescriptor;

  private _parent?: IPointerController;

  public constructor(
    viewPortController: IViewPortController,
    rayCaster: IRaycaster,
    noRotate: boolean,
    noWheelZoom: boolean
  ) {
    this._viewPortController = viewPortController;
    this._cameraController = viewPortController.getCameraController();
    this._noRotate = noRotate;
    this._noWheelZoom = noWheelZoom;
    this._actionFactory = new PointerActionFactory(rayCaster);
  }

  public setNoRotate(noRotate: boolean): void {
    this._noRotate = noRotate;
  }

  // ICameraModifier ------------------------------------------

  public getPointerEventCoordinates(event: PointerEvent | WheelEvent): THREE.Vector2 {
    const { left, top } = (event.target as HTMLElement).getBoundingClientRect();
    const { x, y } = this._cameraController.getCanvas().getBoundingClientRect();
    return new THREE.Vector2(left + event.offsetX - x, top + event.offsetY - y);
  }

  public getFrameDimensions(): THREE.Vector2 {
    return this._cameraController.getFrameDimensions();
  }

  public getCenterPoint(): THREE.Vector3 {
    return this._viewPortController.getCommonCenterPoint();
  }

  public getCameraZoom(): number {
    return this._cameraController.getCameraZoom();
  }

  public rotateCamera(ptOfInterest3d: THREE.Vector3, ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2): void {
    const vec2d = new THREE.Vector2(ptFrom2d.y - ptTo2d.y, ptTo2d.x - ptFrom2d.x); // Perpendicular to (From->To) vector
    const vec3d = this._cameraController.unproject(vec2d).sub(this._cameraController.unproject(new THREE.Vector2()));

    const angleRad = (Math.PI * 2 * vec2d.length()) / this._cameraController.getCanvas().height;
    this._cameraController.rotateCamera(ptOfInterest3d, vec3d, angleRad, false);
  }

  public rotateCameraInPlane(ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2): void {
    const cameraPos = this._cameraController.getCameraPosition();
    const cameraDir = this._cameraController.getCameraDirection();
    const center3d = this._cameraController.unproject(this._cameraController.project(cameraPos));
    const vecFrom3d = this._cameraController.unproject(ptFrom2d).sub(center3d);
    const vecTo3d = this._cameraController.unproject(ptTo2d).sub(center3d);
    let angleRad = vecFrom3d.angleTo(vecTo3d);
    if (new THREE.Vector3().crossVectors(vecFrom3d, vecTo3d).dot(cameraDir) > 0) {
      angleRad = -angleRad;
    }
    this._cameraController.rotateCamera(cameraPos, cameraDir, angleRad, false);
  }

  public rotateCameraInPlaneDirect(
    ptOfInterest2d: THREE.Vector2,
    vecFrom2d: THREE.Vector2,
    vecTo2d: THREE.Vector2
  ): void {
    const ptOfInterest3d = this._cameraController.unproject(ptOfInterest2d);
    const cameraDir = this._cameraController.getCameraDirection();
    let angleRad = vecFrom2d.angleTo(vecTo2d);
    if (vecFrom2d.cross(vecTo2d) > 0) {
      angleRad = -angleRad;
    }
    this._cameraController.rotateCamera(ptOfInterest3d, cameraDir, angleRad, false);
  }

  public panCamera(ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2): void {
    const vec2d = new THREE.Vector2(ptFrom2d.x - ptTo2d.x, ptFrom2d.y - ptTo2d.y);
    const vec3d = this._cameraController.unproject(vec2d).sub(this._cameraController.unproject(new THREE.Vector2()));
    this._cameraController.panCamera(vec3d, false);
  }

  private _zoomCamera(ptOfInterest3d: THREE.Vector3, zoomAmount: number): void {
    let factor = 1 + Math.min(Math.abs(zoomAmount), 100) / 200;
    if (zoomAmount < 0) {
      factor = 1 / factor;
    }
    this._cameraController.zoomCamera(factor, ptOfInterest3d, false);
  }

  public zoomCamera(ptOfInterest2d: THREE.Vector2, ptFrom2d: THREE.Vector2, ptTo2d: THREE.Vector2): void {
    const ptOfInterest3d = this._cameraController.unproject(ptOfInterest2d);
    const vec2d = ptTo2d.clone().sub(ptFrom2d);
    this._zoomCamera(ptOfInterest3d, vec2d.length() * -Math.sign(vec2d.y));
  }

  public zoomCameraDirect(ptOfInterest2d: THREE.Vector2, zoomAmount: number): void {
    const ptOfInterest3d = this._cameraController.unproject(ptOfInterest2d);
    this._zoomCamera(ptOfInterest3d, zoomAmount);
  }

  public notifyCameraChanged(): void {
    this._cameraController.notifyCameraChanged();
  }

  // IPointerHandler ------------------------------------------

  public setParent(parent: IPointerController): void {
    this._parent = parent;
  }

  public onPointerDown(event: PointerSyntheticEvent): void {
    if (!this._parent) {
      throw new Error('Parent expected to be set so far');
    }
    if (this._activeAction === undefined) {
      this._activeAction = this._actionFactory.createPointerAction(
        event,
        this._parent,
        this,
        this._noRotate,
        this._rotateAroundCenterZoom
      );
    }
    this._activeAction.addPointer(event);
  }

  public onPointerUp(event: PointerSyntheticEvent): void {
    if (!this._activeAction) {
      return;
    }
    if (!this._parent) {
      throw new Error('Parent expected to be set so far');
    }
    if (this._activeAction?.removePointer(event) === true) {
      this._activeAction = undefined;
    }
    this._parent?.releasePointerCapture(event.originalEvent.pointerId);
    if (this._handlerLock) {
      this._handlerLock.remove();
      this._handlerLock = undefined;
    }
  }

  public onPointerMove(event: PointerSyntheticEvent): void {
    if (!this._activeAction) {
      return;
    }
    if (!this._parent) {
      throw new Error('Parent expected to be set so far');
    }
    event.stopPropagation();
    event.preventDefault();
    if (!this._handlerLock) {
      this._parent.setPointerCapture(event.originalEvent.pointerId);
      this._handlerLock = this._parent.pushPointerHandler(this);
    }
    if (this._activeAction?.updatePointer(event) === true) {
      this._cameraController.notifyCameraChanged();
    }
  }

  public onPointerCancel(event: PointerSyntheticEvent): void {
    this.onPointerUp(event);
  }

  public onWheel(event: SyntheticEvent<WheelEvent>): void {
    // ctrl + wheel zoom is handled by the browser
    if (!this._activeAction && !this._noWheelZoom && !event.originalEvent.ctrlKey) {
      event.stopPropagation();
      event.preventDefault();
      const { x, y } = this.getPointerEventCoordinates(event.originalEvent);
      const cursor2d = new THREE.Vector2(x, y);
      let amount = -event.originalEvent.deltaY;
      switch (event.originalEvent.deltaMode) {
        case 2:
          // Zoom in pages (default is 1pg)
          amount *= 51;
          break;
        case 1:
          // Zoom in lines (default is 3ln)
          amount *= 17;
          break;
        default:
          // undefined, 0, assume pixels (default is 100px)
          amount *= 0.51;
          break;
      }
      this.zoomCameraDirect(cursor2d, amount);
      this._cameraController.notifyCameraChanged();
    }
  }
}
